import { useMemo, useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import WhatsappService, {
  WhatsappTemplateItemType
} from "../../../services/WhatsappService";
import { Field } from "../../action/Edit/components/FieldHelpers";
import Dropdown from "../../shared/components/Dropdown";
import CompanyService, { Entities } from "../../../services/CompanyService";
import {
  EntityField,
  sortEntityFields
} from "../../shared/entities/EntityField";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";
import { ToastService } from "../../shared/bootstrap/Toast";
import FloatingPanelService from "../../shared/FloatingPanel";
import {
  ClientFields,
  getClientFieldTitle
} from "../../../services/ClientService";

const WhatsappTemplateEdit = ({
  template,
  reload
}: {
  template: WhatsappTemplateItemType;
  reload: () => Promise<void>;
}) => {
  const dataParameters = template.Parameters;
  const elements = Array.from(
    { length: dataParameters || 0 },
    (_, index) => index + 1
  );
  const initialData = useMemo(() => {
    const templateFields = template.FieldList?.split(",") ?? [];
    return elements.map((elIndex) => {
      const fieldId = templateFields[elIndex - 1];
      if (fieldId && fieldId.length > 0) {
        return { param: elIndex, data: `${fieldId}`, valueText: `${fieldId}` };
      }
      return {
        param: elIndex,
        data: `{{${elIndex}}}`,
        valueText: `{{${elIndex}}}`
      };
    });
  }, [elements, template.FieldList]);
  const [data, setData] = useState<any[]>(initialData);
  const [isSaving, setIsSaving] = useState(false);
  const emptyValueDetected = useMemo(
    () => data.some((el) => !el.data || el.data.includes("{{")),
    [data]
  );
  const disableSabeButton = useMemo(
    () => emptyValueDetected,
    [emptyValueDetected]
  );

  const fixedFields = useMemo(() => {
    const clientFieldOptions = [
      ...CompanyService.getAdditionalDefinitionsFiltered(Entities.Client).map(
        (x) => new EntityField(x)
      )
    ]
      .sort(sortEntityFields(CompanyService.getPersonSortedFields().split(",")))
      .map((x) => ({ value: x.id.toString(), text: x?.title ?? "" }));

    const insertFieldOptions = [
      { text: TranslationService.translate.InsertField, value: "" },
      ...WhatsappService.getConfigItemsByWhatsApp().map((cl) => ({
        value: cl.toString(),
        text: getClientFieldTitle(cl as ClientFields)
      })),
      ...clientFieldOptions
    ];
    return insertFieldOptions;
  }, []);

  const templateFormated = useMemo(() => {
    let texto = template.Structure;

    if (template.FieldList) {
      data.forEach(({ param, valueText }) => {
        texto = texto.replace(
          `{{${param}}}`,
          `{{${fixedFields.find((f) => f.value === valueText)?.text || valueText}}}`
        );
      });
    }
    return texto;
  }, [template.Structure, template.FieldList, data, fixedFields]);

  const handleChangeWhatsAppTemplate = async () => {
    setIsSaving(true);
    const CompanyID = CompanyService.getCompanyToken()?.companyid;
    if (CompanyID) {
      const stringDeData = data.map((item) => item.data).join(",");

      const result = await WhatsappService.setTemplate({
        WATemplateID: template.WATemplateID,
        CompanyID: parseInt(CompanyID),
        FieldList: stringDeData
      });
      if (result instanceof Error) {
        ToastService.showToast(
          TranslationService.translate.WhatsAppErrorTemplate,
          "",
          "danger"
        );
        return;
      } else {
        ToastService.showToast(
          TranslationService.translate.WhatsAppTemplateUpdated,
          "",
          "success"
        );
      }
    }
    setIsSaving(false);
    FloatingPanelService.hidePanel();
    await reload();
  };

  return (
    <>
      <div className="floatingBody p-4">
        <div className="row">
          <Field
            colClass="col-8"
            title={TranslationService.translate.WhatsAppTemplate}
          >
            <textarea
              disabled
              className="form-control"
              value={templateFormated}
              style={{ resize: "none" }}
              rows={6}
            />
          </Field>
          <div className="col">
            {elements?.map((elIndex) => (
              <InsertFields
                key={elIndex}
                index={elIndex}
                setData={setData}
                data={data}
                fixedFields={fixedFields}
              />
            ))}
            {disableSabeButton && (
              <p className="text-danger">
                {TranslationService.translate.MissingRequiredFields}
              </p>
            )}
          </div>
        </div>
      </div>
      <FloatingPanelFooter>
        <button
          className="btn btn-primary me-3"
          disabled={disableSabeButton}
          onClick={handleChangeWhatsAppTemplate}
        >
          {TranslationService.translate.Save}
          {isSaving && (
            <i className="fas fa-spinner-third fa-spin third ms-2"></i>
          )}
        </button>
      </FloatingPanelFooter>
    </>
  );
};

const InsertFields = ({
  index = 1,
  data,
  setData,
  fixedFields
}: {
  index: number;
  data: any[];
  setData: (value: any) => void;
  fixedFields: {
    text: string;
    value: string;
  }[];
}) => {
  const currentValue = data.find((e) => e.param === index)?.data;
  const valueDefault = fixedFields.find((e) => e.value === currentValue)?.value;

  const onChange = (value: string) => {
    const dataValue = value?.length > 0 ? value : `{{${index}}}`;
    const valueText =
      value?.length > 0
        ? fixedFields.find((field) => field.value === value)?.text || "Otro"
        : `{{${index}}}`;
    const hasParameter = data.some((el) => el.param === index);
    const newData = hasParameter
      ? data.map((el) => {
          if (el.param === index) {
            return {
              ...el,
              data: dataValue,
              valueText
            };
          }
          return el;
        })
      : [...data, { param: index, data: dataValue, valueText }].sort(
          (a, b) => a.param - b.param
        );

    setData(newData);
  };

  return (
    <Field
      colClass="col"
      title={TranslationService.translate.Field + " " + index}
    >
      <Dropdown
        onChange={onChange}
        items={fixedFields}
        defaultValue={valueDefault}
      />
    </Field>
  );
};

export default WhatsappTemplateEdit;
